import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import {
  Card,
  Chip,
  bySlotOrder,
  dayName,
  relativeDay,
  shortDate,
  slotLabel,
  todayIso,
  useToast,
} from '../components/ui';
import { DishCard } from '../components/DishCard';
import { courseOfSlot, useSwapDrag } from '../components/useSwapDrag';
import { MealPicker } from '../components/MealPicker';
import { BatchPicker } from '../components/BatchPicker';
import type { Dish, Member, WeekView } from '../types';
import type { DashboardModuleProps } from './registry';

const T = {
  en: {
    thisWeek: 'This week',
    generate: 'Plan my week',
    optimise: 'Improve it',
    optimiseHint: 'Keep these meals but shuffle them into a better week',
    replan: 'Re-plan the week',
    replanHint:
      'Re-planning changes every meal except the ones you have kept, already cooked, or already shopped for.',
    working: 'Planning…',
    empty: 'No meals planned yet for this week.',
    lunch: 'Lunch',
    dinner: 'Dinner',
    fishRule: (n: number, min: number) => `Fish: ${n} of ${min} planned`,
    changed: 'Meal swapped',
    swapped: (name: string, where: string) => `${name} moved to ${where}`,
    carrying: (name: string) => `Moving ${name} — now pick the day to swap it with`,
    cancelMove: 'Cancel',
    chosen: 'Meal changed',
    cookSet: (name: string) => `${name} is cooking — and will next week too`,
    cookCleared: 'Nobody assigned',
    batched: (where: string) =>
      where === 'freezer'
        ? 'Set. Cook extra and freeze it — shopped for once.'
        : 'Set. Cook extra and keep it in the fridge — shopped for once.',
    unbatched: 'Back to cooking it fresh',
    kept: 'Kept — this meal stays when you re-plan',
    released: 'Released — this meal can change again',
    movedFish: 'Fish moved to another day to keep the weekly rule',
    needs: (items: string[]) => `Needs one extra item: ${items.join(', ')}`,
    usesEverything: 'Uses everything already bought',
    gone: 'already eaten',
    shopDone: (shop: string) => `${shop} — done. Meals below use what came home.`,
    shopToDo: (shop: string) => `${shop} — still to do. Anything below is open.`,
  },
  fr: {
    thisWeek: 'Cette semaine',
    generate: 'Planifier ma semaine',
    optimise: 'Améliorer',
    optimiseHint: 'Garder ces plats mais mieux les répartir',
    replan: 'Replanifier la semaine',
    replanHint:
      'La replanification change tous les repas sauf ceux que vous avez gardés, déjà cuisinés ou déjà achetés.',
    working: 'Planification…',
    empty: 'Aucun repas prévu cette semaine.',
    lunch: 'Déjeuner',
    dinner: 'Dîner',
    fishRule: (n: number, min: number) => `Poisson : ${n} sur ${min}`,
    changed: 'Repas changé',
    swapped: (name: string, where: string) => `${name} déplacé — ${where}`,
    carrying: (name: string) => `Déplacement de ${name} — choisissez le jour à échanger`,
    cancelMove: 'Annuler',
    chosen: 'Repas modifié',
    cookSet: (name: string) => `${name} cuisine — et la semaine prochaine aussi`,
    cookCleared: 'Personne d’assigné',
    batched: (where: string) =>
      where === 'freezer'
        ? 'C’est noté. Cuisinez plus et congelez — acheté une seule fois.'
        : 'C’est noté. Cuisinez plus et gardez au frais — acheté une seule fois.',
    unbatched: 'Retour à une cuisson fraîche',
    kept: 'Gardé — ce plat ne changera pas',
    released: 'Libéré — ce plat peut de nouveau changer',
    movedFish: 'Le poisson a été déplacé pour respecter la règle',
    needs: (items: string[]) => `Un article à acheter : ${items.join(', ')}`,
    usesEverything: 'Utilise ce qui est déjà acheté',
    gone: 'déjà passé',
    shopDone: (shop: string) => `${shop} — fait. Les repas ci-dessous utilisent ce qui a été acheté.`,
    shopToDo: (shop: string) => `${shop} — à faire. Tout ce qui suit reste ouvert.`,
  },
} as const;

/** The week at a glance, with reroll and lock on every dish (§33-36). */
export function WeekModule({ week, language, refresh }: DashboardModuleProps) {
  const t = T[language];
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState<Dish | null>(null);
  const [batching, setBatching] = useState<Dish | null>(null);
  const [people, setPeople] = useState<Member[]>([]);

  // Who could be cooking. Children are included on purpose — a twelve-year-old
  // making Friday pasta is exactly the sort of thing this should record.
  useEffect(() => {
    void api.household().then((result) => setPeople(result.members));
  }, []);
  const today = todayIso();

  // Dragging one meal onto another (§34). Set up before the early return
  // below, because a hook that only sometimes runs is not a hook — which is
  // also why the week and the action runner arrive through refs: both are
  // rebuilt on every render, further down.
  const weekRef = useRef(week);
  weekRef.current = week;
  const runRef = useRef<(action: () => Promise<WeekView>, message?: string) => Promise<void>>(
    async () => {},
  );
  const swapDishes = useCallback(
    (fromKey: string, toKey: string) => {
      const current = weekRef.current;
      if (!current) return;
      void runRef.current(async () => {
        const result = await api.swap(current.weekStart, fromKey, toKey, language);
        if (result.movedTo) {
          toast(
            t.swapped(
              result.movedTo.mealName,
              `${dayName(result.movedTo.date, language)} · ${result.movedTo.slotLabel.toLowerCase()}`,
            ),
          );
        }
        return result;
      });
    },
    [language, t, toast],
  );
  const drag = useSwapDrag(swapDishes, { enabled: !busy });

  if (!week) return null;

  const hasMeals = week.days.some((day) => day.slots.some((slot) => slot.dishes.some((d) => d.meal)));

  const run = async (action: () => Promise<WeekView>, successMessage?: string) => {
    setBusy(true);
    try {
      const result = await action();
      if (result.extraIngredients?.length) {
        toast(t.needs(result.extraIngredients.map((i) => i.name)));
      } else if (result.postShop) {
        toast(t.usesEverything);
      } else if (result.movedFishTo) {
        toast(t.movedFish);
      } else if (successMessage) {
        toast(successMessage);
      }
      refresh();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  runRef.current = run;

  const onReroll = (dish: Dish) =>
    run(() => api.reroll(week.weekStart, dish.assignmentKey, language), t.changed);

  const onChoose = (dish: Dish) => setPicking(dish);
  const onBatch = (dish: Dish) => setBatching(dish);

  const onUnbatch = (dish: Dish) => {
    if (!dish.plannedMealId) return;
    void run(
      () => api.unlinkBatch(week.weekStart, dish.plannedMealId!, language),
      t.unbatched,
    );
  };

  const onSetCook = (dish: Dish, memberId: string | null) => {
    if (!dish.plannedMealId) return;
    const name = people.find((p) => p.id === memberId)?.name ?? '';
    void run(
      () => api.setCook(week.weekStart, dish.plannedMealId!, memberId, language),
      memberId ? t.cookSet(name) : t.cookCleared,
    );
  };

  const onLock = (dish: Dish) =>
    dish.plannedMealId
      ? run(
          () => api.lock(week.weekStart, dish.plannedMealId!, !dish.locked, language),
          dish.locked ? t.released : t.kept,
        )
      : undefined;

  const onFavourite = async (dish: Dish) => {
    if (!dish.meal) return;
    await api.favourite(dish.meal.id, !dish.meal.isFavourite);
    refresh();
  };

  return (
    <Card
      title={
        <div>
          <h2>{t.thisWeek}</h2>
          <div className="row" style={{ gap: 6, marginTop: 4 }}>
            <Chip kind={week.fishPlanned >= week.fishMinimum ? 'fish' : 'fav'}>
              {t.fishRule(week.fishPlanned, week.fishMinimum)}
            </Chip>
            {week.status !== 'draft' && <Chip>{week.status.replace('_', ' ')}</Chip>}
          </div>
        </div>
      }
      actions={
        <div className="row" style={{ gap: 8 }}>
          {hasMeals && (
            <button
              className="btn"
              disabled={busy}
              title={t.optimiseHint}
              onClick={() => run(() => api.optimise(week.weekStart, language))}
            >
              {busy ? t.working : t.optimise}
            </button>
          )}
          <button
            className="btn primary"
            disabled={busy}
            title={hasMeals ? t.replanHint : undefined}
            onClick={() => run(() => api.generate(week.weekStart, language))}
          >
            {busy ? t.working : hasMeals ? `↻ ${t.replan}` : t.generate}
          </button>
        </div>
      }
      tight
    >
      {hasMeals && (
        <p className="dim" style={{ padding: '0 18px 10px', fontSize: 13, margin: 0 }}>
          {t.replanHint}
        </p>
      )}

      {/* Held by a tap rather than a drag: the finger is free, so the screen
          has to say what it is waiting for. */}
      {drag.held && !drag.dragging && (
        <div className="carrying">
          <span>{t.carrying(drag.held.name)}</span>
          <button className="btn" onClick={drag.cancel}>
            {t.cancelMove}
          </button>
        </div>
      )}

      {!hasMeals ? (
        <div className="empty">{t.empty}</div>
      ) : (
        <div style={{ margin: '0 -18px' }}>
          {week.days.map((day) => {
            if (day.slots.length === 0) return null;
            const relative = relativeDay(day.date, today, language);
            // Shops are the boundaries of the plan: everything after a shop
            // that is done is settled, everything after one still to do is
            // genuinely open (§43-44).
            const shops = week.trips.filter((trip) => trip.enabled && trip.date === day.date);
            return (
              <div
                key={day.date}
                className={day.isPast ? 'day-past' : undefined}
                style={{ borderTop: '1px solid var(--line)' }}
              >
                {shops.map((trip) => (
                  <div key={trip.id} className={`shop-divider${trip.completed ? ' done' : ''}`}>
                    {trip.completed ? t.shopDone(trip.shopLabel) : t.shopToDo(trip.shopLabel)}
                  </div>
                ))}
                <div className={`day-head${day.isToday ? ' today' : ''}`} style={{ padding: '14px 18px 0' }}>
                  <span className="date">{dayName(day.date, language)}</span>
                  <span className="rel">
                    {shortDate(day.date, language)}
                    {relative ? ` · ${relative}` : ''}
                    {day.isPast && ` · ${t.gone}`}
                  </span>
                </div>
                {[...day.slots].sort(bySlotOrder).map((slot) =>
                  slot.dishes.map((dish, index) => (
                    <div
                      key={dish.assignmentKey}
                      // What the drag reads to know where the finger is (§34).
                      data-dish-key={dish.assignmentKey}
                      data-dish-course={courseOfSlot(slot.slotType)}
                      data-dish-droppable={
                        !day.isPast && !dish.locked && !dish.batch && !dish.cookedAt
                      }
                      className={[
                        drag.over === dish.assignmentKey ? 'drop-here' : '',
                        drag.held?.assignmentKey === dish.assignmentKey ? 'lifted' : '',
                        drag.held && drag.canDropOn({ ...dish, slotType: slot.slotType })
                          ? 'droppable'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {index === 0 && (
                        <div className="audience-tag" style={{ padding: '10px 18px 0' }}>
                          {slotLabel(slot.slotType, language)}
                        </div>
                      )}
                      <DishCard
                        dish={dish}
                        language={language}
                        showAudience={slot.dishes.length > 1}
                        busy={busy}
                        onReroll={day.isPast ? undefined : onReroll}
                        onChoose={day.isPast ? undefined : onChoose}
                        onSetCook={onSetCook}
                        onBatch={day.isPast ? undefined : onBatch}
                        onUnbatch={day.isPast ? undefined : onUnbatch}
                        cooks={people.map((p) => ({ id: p.id, name: p.name }))}
                        onLock={dish.plannedMealId && !day.isPast ? onLock : undefined}
                        onFavourite={onFavourite}
                        held={drag.held?.assignmentKey === dish.assignmentKey}
                        grip={
                          day.isPast || dish.locked || dish.batch || dish.cookedAt || !dish.meal
                            ? undefined
                            : drag.gripProps({
                                assignmentKey: dish.assignmentKey,
                                slotType: slot.slotType,
                                name: dish.meal.name,
                              })
                        }
                      />
                    </div>
                  )),
                )}
              </div>
            );
          })}
        </div>
      )}

      {drag.dragging && drag.point && drag.held && (
        <div
          className="drag-ghost"
          style={{ left: drag.point.x, top: drag.point.y }}
          aria-hidden="true"
        >
          {drag.held.name}
        </div>
      )}

      {batching && (
        <BatchPicker
          weekStart={week.weekStart}
          assignmentKey={batching.assignmentKey}
          language={language}
          onClose={() => setBatching(null)}
          onLink={(targetKey, becomesMealId) => {
            const sourceKey = batching.assignmentKey;
            setBatching(null);
            void run(async () => {
              const result = await api.linkBatch(
                week.weekStart,
                sourceKey,
                targetKey,
                becomesMealId,
                language,
              );
              toast(t.batched(result.batchStorage));
              return result;
            });
          }}
        />
      )}

      {picking && (
        <MealPicker
          weekStart={week.weekStart}
          assignmentKey={picking.assignmentKey}
          language={language}
          onClose={() => setPicking(null)}
          onChoose={(mealId) => {
            const key = picking.assignmentKey;
            setPicking(null);
            void run(() => api.setMeal(week.weekStart, key, mealId, language), t.chosen);
          }}
        />
      )}
    </Card>
  );
}
